import { IonButton, IonButtons, IonCol, IonContent, IonGrid, IonHeader, IonRow, IonToolbar } from '@ionic/react';
import { InnerhivePage } from '../InnerhivePage';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { ResourcesState } from './ResourcesState';
import { useSubscription } from '@meraki-internal/state';
import { createUseStyles } from 'react-jss';
import { mediaMaxWidth } from '../theme/utils';
import { Text } from '../components/Text';
import { Spinner } from '../components/Spinner';
import { ArrowLeftIcon } from '../theme/Icons';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { useEffect } from 'react';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { ResourceRating } from './ResourceRating';
import { useToast } from '../components/useToast';
import { ShareResourceButton } from './ShareResourceButton';

const useStyles = createUseStyles(() => ({
    toolbar: {
        // partially transparent
        '--background': 'rgba(255, 255, 255, 0.85)'
    },

    content: {
        // use same max width as webflow
        maxWidth: 1200,
        justifySelf: 'center',

        '--padding-start': '10px',
        '--padding-end': '10px',

        // We don't want the padding applied by InnerhivePage so we opt out with skipAddingIonContent,
        // but this causes InnerhivePage to wrap a <div> around our IonHeader/IonContent, which
        // causes IonContent to fail to account for IonHeader height, so we need to do that here
        '--padding-top': 'calc(56px + var(--ion-safe-area-top)) !important'
    },

    mainImage: {
        // 1st of 2 cols
        paddingRight: 20,

        // single col full width
        [mediaMaxWidth.md]: {
            paddingRight: 'inherit',
            paddingBottom: 10,
            marginLeft: -26,
            marginRight: -26,
            width: 'fill-available',
            maxWidth: 'calc(100% + 52px)',
            '& img': { width: '100%' }
        }
    },

    logo: {
        display: 'flex',
        justifyContent: 'center',
        paddingBottom: 10,
        '& img': { maxHeight: 150 }
    }
}));

export const ResourceDetailsPage: React.FC<{slug: string}> = ({ slug }) => {
    const classes = useStyles();

    const history = useInstance(HistoryViewModel);

    const mixpanel = useInstance(MixPanelEventEmitter);

    const [showToast] = useToast();

    const resourcesState = useInstance(ResourcesState);
    useSubscription(() => resourcesState);

    const resource = resourcesState.getResourceBySlug(slug);

    useEffect(() => {
        if (resource) {
            mixpanel.track('Resource Opened', () => ({
                Name: resource?.name,
                Category: resource?.category
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resource?.slug]);

    const onRate = async ({ resourceId, rating }: { resourceId: string, rating?: number }) => {
        try {
            await resourcesState.rateResource({ resourceId, rating });
        } catch (err: any) {
            showToast({ message: err.displayMessage || 'Sorry something went wrong', duration: 3000 });
        }
    };

    const onWebsiteOpened = () => {
        mixpanel.track('Resource Website Opened', () => ({
            'Name': resource!.name,
            'Category': resource!.category,
            'Opened From': 'Details'
        }));
    };

    return (
        <InnerhivePage skipAddingIonContent hideAppHeader>

            <IonContent data-type="resource-details" className={classes.content}>

                <IonHeader slot="fixed">
                    <IonToolbar className={classes.toolbar}>
                        <IonButtons slot="start">
                            <IonButton data-type="back-button" fill="clear" shape="round" onClick={() => history.back({ fallbackPath: '/resources' })}>
                                <ArrowLeftIcon color="dark" />
                            </IonButton>
                        </IonButtons>
                        <IonButtons slot="end" style={{marginRight: 4}}>
                            {resource &&
                                <ShareResourceButton
                                    data-type="share-header-button"
                                    shape="round"
                                    iconColor="dark"
                                    resource={resource}
                                    trackingFrom="Header"
                                />
                            }
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>

                {!resourcesState.isLoaded() &&
                    <Spinner centered />
                }

                {resourcesState.isLoaded() && !resource &&
                    <div style={{margin: 10}}>
                        <Text>Sorry, this resource was not found.</Text>
                    </div>
                }

                {resource &&
                    <IonGrid style={{marginTop: -10}}>
                        <IonRow>

                            <IonCol size="12" sizeMd={resource.mainImageUrl ? '6' : '3'}>
                                <div className={classes.mainImage}>
                                    <img src={resource.mainImageUrl} alt="" />
                                </div>
                            </IonCol>

                            <IonCol size="12" sizeMd={'6'}>

                                <div className={classes.logo} >
                                    <img src={resource.logoUrl} alt="logo" />
                                </div>

                                <div style={{paddingBottom: 10, cursor: 'pointer'}} onClick={() => resourcesState.filterTo({ category: resource.category, trackingFrom: 'Details' })}>
                                    <Text type="subtitle"><span style={{fontSize: 14}}>{resource.category.toUpperCase()}</span></Text>
                                </div>

                                <Text type="title">{resource.name}</Text>

                                <div style={{margin: '10px 0 -5px'}}>
                                    <ResourceRating
                                        resource={resource}
                                        onRate={(rating) => onRate({ resourceId: resource.resourceId, rating })}
                                        trackingFrom="Details"
                                    />
                                </div>

                                <div dangerouslySetInnerHTML={{ __html: resource.contentHtml }} />

                                <div style={{display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between', marginTop: 20}}>
                                    <IonButton mode="ios" fill="outline" href={resource.website} onClick={onWebsiteOpened} target="_blank">
                                        Visit Website
                                    </IonButton>

                                    <ShareResourceButton
                                        data-type="share-button"
                                        mode="ios"
                                        color="light"
                                        buttonLabel="Share"
                                        resource={resource}
                                        trackingFrom="Details"
                                    />
                                </div>

                                <div style={{marginTop: 15, marginBottom: 15}}>
                                    <Text>
                                        <span style={{fontSize: 14}}>
                                            Have a resource to recommend? Suggest one <a href="https://tally.so/r/mYxvg0" target="_blank">here</a>!
                                        </span>
                                    </Text>
                                </div>

                            </IonCol>

                        </IonRow>
                    </IonGrid>
                }

            </IonContent>

        </InnerhivePage>
    );
};
